import { Environment } from './clientTypes';

const domains = {
  development: 'local.agora.upgrade.education:8443',
  staging: 'staging.agora.upgrade.education',
  CI: 'ci.agora.upgrade.education',
  production: 'agora.upgrade.education',
};

const baseUrls = {
  development: 'https://' + domains.development,
  staging: 'https://' + domains.staging,
  CI: 'https://' + domains.CI,
  production: 'https://' + domains.production,
};

const isEnvironment = (value: unknown): value is Environment =>
  value === 'development' || value === 'staging' || value === 'CI' || value === 'production';

const getInitialEnvironment = (): Environment => {
  const env = process.env.NX_ENVIRONMENT as Environment | undefined;
  return isEnvironment(env) ? env : 'development';
};

const computeBaseUrl = (environment: Environment) => baseUrls[environment] ?? baseUrls.production;
const computeDomain = (environment: Environment) => domains[environment] ?? domains.production;

export type FrontendRuntimeConfig = {
  ENVIRONMENT?: Environment;
  BASE_URL?: string;
  DOMAIN?: string;
  AUTH0_CLIENT_ID?: string;
  AUTH0_DOMAIN?: string;
  AUTH0_AUDIENCE?: string;
  AUTH0_CONNECTION?: string;
  USE_AUTH0?: string;
  FIRST_UPLOAD_INVOICE_DAY?: number | string;
  LAST_UPLOAD_INVOICE_DAY?: number | string;
  G_ANALYTICS_ID?: string;
  HOTJAR_ID?: number | string;
  NEW_RELIC_APP_ID?: string;
  NEW_RELIC_LICENSE_KEY?: string;
  NEW_RELIC_ACCOUNT_ID?: string;
  NEW_RELIC_BEACON?: string;
  SPACES_HOST?: string;
};

const warn = (message: string) => {
  if (typeof console !== 'undefined' && typeof console.warn === 'function') {
    console.warn(`[RuntimeConfig] ${message}`);
  }
};

const toNumberOrUndefined = (
  value: number | string | undefined,
  fieldName?: string
): number | undefined => {
  if (value === null || value === undefined || value === '') {
    return undefined;
  }

  const parsed = Number(value);
  if (!Number.isFinite(parsed)) {
    if (fieldName) {
      warn(`Invalid numeric value "${value}" for ${fieldName}.`);
    }
    return undefined;
  }
  return parsed;
};

export let ENVIRONMENT: Environment = getInitialEnvironment();
export let AUTH0_CLIENT_ID: string | undefined = process.env.NX_AUTH0_CLIENT_ID;
export let AUTH0_DOMAIN: string | undefined = process.env.NX_AUTH0_DOMAIN;
export let AUTH0_AUDIENCE: string | undefined = process.env.NX_AUTH0_AUDIENCE;
export let AUTH0_CONNECTION: string | undefined = process.env.NX_AUTH0_CONNECTION;
export let USE_AUTH0: string = process.env.NX_USE_AUTH0 ?? '1';

export let BASE_URL: string = computeBaseUrl(ENVIRONMENT);
export let DOMAIN: string = computeDomain(ENVIRONMENT);

export let isDevelopment: boolean = ENVIRONMENT === 'development';
export let isStaging: boolean = ENVIRONMENT === 'staging';
export let isCi: boolean = ENVIRONMENT === 'CI';

export const MAIN_WEBSITE_DOMAIN = 'upgrade.education';

export let FIRST_UPLOAD_INVOICE_DAY: number | undefined = toNumberOrUndefined(
  process.env.NX_FIRST_UPLOAD_INVOICE_DAY,
  'NX_FIRST_UPLOAD_INVOICE_DAY'
);
export let LAST_UPLOAD_INVOICE_DAY: number | undefined = toNumberOrUndefined(
  process.env.NX_LAST_UPLOAD_INVOICE_DAY,
  'NX_LAST_UPLOAD_INVOICE_DAY'
);

export let G_ANALYTICS_ID: string | undefined = process.env.NX_G_ANALYTICS_ID;
export let HOTJAR_ID: number | undefined = toNumberOrUndefined(
  process.env.NX_HOTJAR_ID,
  'NX_HOTJAR_ID'
);

export let NEW_RELIC_APP_ID: string | undefined = process.env.NX_NEW_RELIC_APP_ID;
export let NEW_RELIC_LICENSE_KEY: string | undefined = process.env.NX_NEW_RELIC_LICENSE_KEY;
export let NEW_RELIC_ACCOUNT_ID: string | undefined = process.env.NX_NEW_RELIC_ACCOUNT_ID;
export let NEW_RELIC_BEACON: string | undefined = process.env.NX_NEW_RELIC_BEACON;

export let SPACES_HOST: string | undefined = process.env.NX_SPACES_HOST;

const recomputeDerivedFlags = () => {
  isDevelopment = ENVIRONMENT === 'development';
  isStaging = ENVIRONMENT === 'staging';
  isCi = ENVIRONMENT === 'CI';
  if (!BASE_URL) {
    BASE_URL = computeBaseUrl(ENVIRONMENT);
  }

  if (!DOMAIN) {
    DOMAIN = computeDomain(ENVIRONMENT);
  }
};

recomputeDerivedFlags();

export const applyRuntimeFrontendConfig = (
  config: Partial<FrontendRuntimeConfig> | undefined
): void => {
  if (!config) {
    return;
  }

  let environmentOverridden = false;

  if (config.ENVIRONMENT) {
    if (isEnvironment(config.ENVIRONMENT)) {
      ENVIRONMENT = config.ENVIRONMENT;
      environmentOverridden = true;
    } else {
      warn(`Ignored invalid ENVIRONMENT value "${config.ENVIRONMENT}".`);
    }
  }

  if (typeof config.AUTH0_CLIENT_ID === 'string') {
    AUTH0_CLIENT_ID = config.AUTH0_CLIENT_ID;
  }

  if (typeof config.AUTH0_DOMAIN === 'string') {
    AUTH0_DOMAIN = config.AUTH0_DOMAIN;
  }

  if (typeof config.AUTH0_AUDIENCE === 'string') {
    AUTH0_AUDIENCE = config.AUTH0_AUDIENCE;
  }

  if (typeof config.AUTH0_CONNECTION === 'string') {
    AUTH0_CONNECTION = config.AUTH0_CONNECTION;
  }

  if (typeof config.USE_AUTH0 === 'string') {
    USE_AUTH0 = config.USE_AUTH0;
  }

  if (config.BASE_URL !== undefined && config.BASE_URL !== null && config.BASE_URL !== '') {
    BASE_URL = config.BASE_URL;
  } else if (environmentOverridden) {
    BASE_URL = computeBaseUrl(ENVIRONMENT);
  } else if (config.BASE_URL !== undefined) {
    warn('Received empty BASE_URL in runtime config. Falling back to default.');
  }

  if (config.DOMAIN !== undefined && config.DOMAIN !== null && config.DOMAIN !== '') {
    DOMAIN = config.DOMAIN;
  } else if (environmentOverridden) {
    DOMAIN = computeDomain(ENVIRONMENT);
  } else if (config.DOMAIN !== undefined) {
    warn('Received empty DOMAIN in runtime config. Falling back to default.');
  }

  const firstUploadDay = toNumberOrUndefined(
    config.FIRST_UPLOAD_INVOICE_DAY,
    'FIRST_UPLOAD_INVOICE_DAY'
  );
  if (firstUploadDay !== undefined) {
    FIRST_UPLOAD_INVOICE_DAY = firstUploadDay;
  }

  const lastUploadDay = toNumberOrUndefined(
    config.LAST_UPLOAD_INVOICE_DAY,
    'LAST_UPLOAD_INVOICE_DAY'
  );
  if (lastUploadDay !== undefined) {
    LAST_UPLOAD_INVOICE_DAY = lastUploadDay;
  }

  if (typeof config.G_ANALYTICS_ID === 'string') {
    G_ANALYTICS_ID = config.G_ANALYTICS_ID;
  }

  const hotjarId = toNumberOrUndefined(config.HOTJAR_ID, 'HOTJAR_ID');
  if (hotjarId !== undefined) {
    HOTJAR_ID = hotjarId;
  }

  if (typeof config.NEW_RELIC_APP_ID === 'string') {
    NEW_RELIC_APP_ID = config.NEW_RELIC_APP_ID;
  }

  if (typeof config.NEW_RELIC_LICENSE_KEY === 'string') {
    NEW_RELIC_LICENSE_KEY = config.NEW_RELIC_LICENSE_KEY;
  }

  if (typeof config.NEW_RELIC_ACCOUNT_ID === 'string') {
    NEW_RELIC_ACCOUNT_ID = config.NEW_RELIC_ACCOUNT_ID;
  }

  if (typeof config.NEW_RELIC_BEACON === 'string') {
    NEW_RELIC_BEACON = config.NEW_RELIC_BEACON;
  }

  if (typeof config.SPACES_HOST === 'string') {
    SPACES_HOST = config.SPACES_HOST;
  }

  recomputeDerivedFlags();
};
